import React, { useEffect } from 'react';
import { Button, Col, Form, Input, Row, Select, Spin } from 'antd';
import { useParams } from 'react-router-dom';
import PageWithFilters from '../page/page-with-filters';
import { PageWithFiltersContentContainer } from '../page/page.styles';
import useToastContext from '../../hooks/useToastContext';
import useGetRequestUpgrade from '../../hooks/utils/useGetRequestUpgrade';
import usePutRequestUpgrade from '../../hooks/utils/usePutRequestUpgrade';
import { validateStatus } from '../../utils/utils';
import { UsageLimitState } from './UsageLimitState';
import { UsageLimits } from './usage-limits';
import { Countries, CountriesAlphaTwo, StatesByCountry } from '../../constants';
import { useAppContext } from '../../context/appContext';
import { IssuersBrands } from '../../utils/IssuersBrands';

const contentStyle: React.CSSProperties = {
  padding: 200,
  background: 'rgba(0, 0, 0, 0.05)',
  borderRadius: 4,
};

const content = <div style={contentStyle} />;

const UsageLimitPage = () => {
  const {
    settings: { issuer },
  } = useAppContext();
  const toast = useToastContext();
  const [form] = Form.useForm();
  const { usageLimitId } = useParams<{ usageLimitId: string }>();
  const { getData: limit, getLoading} = useGetRequestUpgrade({ path: `v1/usage_limits/${usageLimitId}` });
  const { putError, putLoading, executePut } = usePutRequestUpgrade({ path: `v1/usage_limits/${usageLimitId}` });

  useEffect(() => {
    form.setFieldValue('name', limit?.name);
    form.setFieldValue('created_timestamp', limit?.created_timestamp);
    form.setFieldValue('allow_ecommerce', limit?.allow_ecommerce);
    form.setFieldValue('allow_purchase_chip', limit?.allow_purchase_chip);
    form.setFieldValue('allow_purchase_contactless', limit?.allow_purchase_contactless);
    form.setFieldValue('allow_atm_operation', limit?.allow_atm_operation);
    form.setFieldValue('allow_atm_withdrawal', limit?.allow_atm_withdrawal);
    form.setFieldValue('allow_oct', limit?.allow_oct);
    form.setFieldValue('allow_purchase_magnetic_stripe', limit?.allow_purchase_magnetic_stripe);
    form.setFieldValue('banned_mcc_list', limit?.banned_mcc_list);
    form.setFieldValue('banned_tcc_list', limit?.banned_tcc_list);
    form.setFieldValue('countries_black_list', limit?.countries_black_list);
    form.setFieldValue('countries_white_list', limit?.countries_white_list);
  }, [limit]);

  const onSubmit = async (data: UsageLimits) => {
    try {
      const response = await executePut({ data: data });

      if (validateStatus(response?.status)) {
        toast.success('Exito', 'Limitador de uso actualizado correctamente!.');
      } else {
        throw new Error();
      }

    } catch (e: unknown) {
      toast.error('Error', `Ocurrió un error: ${putError}, porfavor, intenta nuevamente.`);
    }
  }

  return (
    <PageWithFilters title="Limitador de uso">
      <PageWithFiltersContentContainer>
        {
          getLoading ? (
            <Spin tip="Loading" size="large">
              {content}
            </Spin>
          ) : (
            <Form<UsageLimits>
              layout="vertical"
              onFinish={onSubmit}
              form={form}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item<UsageLimits>
                    label="Nombre"
                    name="name"
                    initialValue={ limit?.name }
                    rules={[{ required: true }]}

                  >
                    <Input disabled />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Compras en comercio electrónico"
                    name="allow_ecommerce"
                    initialValue={ limit?.allow_ecommerce }
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Seleccionar tipo"
                      showSearch
                      options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
                    />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Compras con chip"
                    name="allow_purchase_chip"
                    initialValue={ limit?.allow_purchase_chip }
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Seleccionar tipo"
                      showSearch
                      options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
                    />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Compras con contactless"
                    name="allow_purchase_contactless"
                    initialValue={ limit?.allow_purchase_contactless }
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Seleccionar tipo"
                      showSearch
                      options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
                    />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Consulta de Saldo/Operaciones en ATM"
                    name="allow_atm_operation"
                    initialValue={ limit?.allow_atm_operation }
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Seleccionar tipo"
                      showSearch
                      options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
                    />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Extraccion por cajeros"
                    name="allow_atm_withdrawal"
                    initialValue={ limit?.allow_atm_withdrawal }
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Seleccionar tipo"
                      showSearch
                      options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
                    />
                  </Form.Item>
                </Col>
                <Col  span={12}>
                  <Form.Item<UsageLimits>
                    label="Códigos de MCC bloqueados"
                    name="banned_mcc_list"
                    rules={[{ required: true }]}
                  >
                    <Select
                      mode="tags"
                      placeholder="Seleccionar tipo"
                      showSearch
                      options={Object.values(limit?.banned_mcc_list || []).map((type) => ({ label: type, value: type }))}
                    />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Códigos de TCC bloqueados"
                    name="banned_tcc_list"
                  >
                    <Select
                      mode="tags"
                      placeholder="Seleccionar tipo"
                      showSearch
                      options={Object.values(limit?.banned_tcc_list || []).map((type) => ({ label: type, value: type }))}
                    />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Paises Bloqueados"
                    name="countries_black_list"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Seleccionar tipo"
                      showSearch
                      defaultValue={limit?.countries_black_list || []}
                      options={Object.entries(IssuersBrands["VISA"].includes(issuer?.name?.toUpperCase()) ? CountriesAlphaTwo : Countries).map(([code, name]) => ({ label: name, value: code }))}
                    />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Paises habilitados"
                    name="countries_white_list"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Seleccionar tipo"
                      showSearch
                      defaultValue={limit?.countries_white_list || []}
                      options={Object.entries(IssuersBrands["VISA"].includes(issuer.name?.toUpperCase()) ? CountriesAlphaTwo : Countries).map(([code, name]) => ({ label: name, value: code }))}
                    />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Operaciones OCT"
                    name="allow_oct"
                    initialValue={ limit?.allow_oct }
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Seleccionar tipo"
                      showSearch
                      options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
                    />
                  </Form.Item>
                  <Form.Item<UsageLimits>
                    label="Compras con banda magnetica"
                    name="allow_purchase_magnetic_stripe"
                    initialValue={ limit?.allow_purchase_magnetic_stripe }
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Seleccionar tipo"
                      showSearch
                      options={Object.values(UsageLimitState).map((type) => ({ label: type, value: type }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={24} style={{ textAlign: 'right' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={putLoading}
                    style={{ padding: '10px' }}
                  >
                    Guardar
                  </Button>
                </Col>
              </Row>
            </Form>
          )
        }

      </PageWithFiltersContentContainer>
    </PageWithFilters>
  )
}

export default UsageLimitPage;